// Dépôts de fichiers — contrôle CÔTÉ ÉCRAN (lot SEC-10 b).
//
// Ce module ne décide rien : le serveur revalide tout, taille ET contenu réel
// (`backend/src/utils/depots.ts`, `office.ts`, `equipement.ts`). Il existe pour
// que le refus soit IMMÉDIAT et LISIBLE — dire « trop lourd » après avoir fait
// remonter trente mégaoctets encodés en base64, c'est faire attendre pour rien,
// et c'est exactement le moment où l'ancienne version répondait « Erreur
// serveur ».
//
// ⚠️ Les bornes DOIVENT rester identiques à celles du serveur. Elles sont
// écrites ici en clair, avec le nom de leur jumelle côté serveur en regard.

/** Poids lisible, virgule décimale française : « 7,4 Mo ». */
export const poidsMo = (octets: number) => `${(octets / 1048576).toFixed(1).replace('.', ',')} Mo`;

/** Limite lisible, sans décimale inutile : « 8 Mo ». */
export const limiteMo = (octets: number) => {
  const mo = octets / 1048576;
  return `${Number.isInteger(mo) ? mo : mo.toFixed(1).replace('.', ',')} Mo`;
};

/** Bornes par usage — jumelles de `USAGES` / `PIECE_MAX_BYTES` / `OFFICE_MAX_BYTES`. */
export const BORNES = {
  /** `USAGES.pieceRh.max` — CIN, RIB, diplôme, justificatif d'absence. */
  pieceRh: 8 * 1024 * 1024,
  /** `OFFICE_MAX_BYTES` (office.ts) — .docx / .xlsx. */
  bureautique: 25 * 1024 * 1024,
  /** `PIECE_MAX_BYTES` (equipement.ts) — certificats, notices, plans. */
  technique: 25 * 1024 * 1024,
  /** `OFFICE_MAX_BYTES` (office.ts) — exemplaire d'un document externe. */
  documentExterne: 25 * 1024 * 1024,
  /** `USAGES.apercuDocument.max` — aperçu animé d'un document GED (19/10/2026). */
  apercuDocument: 5 * 1024 * 1024,
} as const;

/** Lecture d'une tranche d'octets en texte latin-1 (comparaison de signature). */
const txt = (t: Uint8Array, d: number, f: number) => String.fromCharCode(...Array.from(t.slice(d, f)));

/** Signatures acceptées, par famille. Le nom du fichier n'entre PAS en compte. */
const SIGNATURES: Record<string, (t: Uint8Array) => boolean> = {
  pdf: (t) => txt(t, 0, 5) === '%PDF-',
  jpg: (t) => t[0] === 0xff && t[1] === 0xd8 && t[2] === 0xff,
  png: (t) => t[0] === 0x89 && txt(t, 1, 4) === 'PNG',
  gif: (t) => /^GIF8[79]a$/.test(txt(t, 0, 6)),
  webp: (t) => txt(t, 0, 4) === 'RIFF' && txt(t, 8, 12) === 'WEBP',
  heic: (t) => txt(t, 4, 8) === 'ftyp',
  // .docx / .xlsx sont des archives ZIP : « PK\x03\x04 ». La distinction entre
  // les deux exige de lire l'archive — c'est le serveur qui la fait, ici on ne
  // sépare pas un classeur d'un document, on écarte ce qui n'est pas OOXML.
  ooxml: (t) => t[0] === 0x50 && t[1] === 0x4b && t[2] === 0x03 && t[3] === 0x04,
};


const FAMILLES = {
  /** Pièce du dossier RH, justificatif : ce qui se scanne ou se photographie. */
  pieceRh: {
    formats: ['pdf', 'jpg', 'png', 'webp', 'heic'],
    refus: 'Formats acceptés pour une pièce du dossier : PDF, JPG, PNG, HEIC.',
    conseil: 'Scannez en 200 dpi plutôt qu’en 600, ou déposez un PDF compressé.',
  },
  bureautique: {
    formats: ['ooxml'],
    refus: 'Formats acceptés : .docx, .xlsx (documents Office).',
    conseil: 'Allégez le document (images incorporées), ou scindez-le.',
  },
  technique: {
    formats: ['pdf', 'jpg', 'png'],
    refus: 'Formats acceptés : PDF, JPG, PNG.',
    conseil: 'Recompressez le PDF, ou scannez en 200 dpi plutôt qu’en 600.',
  },
  /** EXEMPLAIRE d'un document EXTERNE : référentiel acheté, notice fournisseur,
   *  certificat. Le PDF y est admis là où il ne l'est pas pour un document que
   *  le laboratoire rédige — un externe ne se travaille pas, il se dépose et se
   *  consulte. Le .docx/.xlsx reste possible : certains fournisseurs livrent
   *  leurs formulaires en bureautique. */
  documentExterne: {
    formats: ['pdf', 'ooxml'],
    refus: 'Formats acceptés pour un document externe : PDF, .docx, .xlsx.',
    conseil: 'Déposez le PDF fourni par l’éditeur, sans le recomposer.',
  },
  /** APERÇU ANIMÉ d'un document (19/10/2026) : une image qui le résume, montrée au survol. */
  apercuDocument: {
    formats: ['gif', 'png', 'webp', 'jpg'],
    refus: 'L’aperçu animé doit être une image : GIF animé, PNG, WEBP ou JPG.',
    conseil: 'Réduisez le nombre d’images ou leur taille (moins de 1 000 pixels de large suffit à l’écran).',
  },
} as const;

export type Famille = keyof typeof FAMILLES;

/**
 * Contrôle un fichier choisi à l'écran. Renvoie le message de refus À AFFICHER,
 * ou `null` si le fichier passe.
 *
 * Le message nomme systématiquement les trois choses qui permettent d'agir :
 * ce qui est refusé, le poids réel du fichier, la limite.
 */
export async function refusDepot(famille: Famille, fichier: File, quoi = 'Le fichier'): Promise<string | null> {
  const max = BORNES[famille];
  const f = FAMILLES[famille];
  if (!fichier.size) return `${quoi} est vide.`;
  if (fichier.size > max) return `${quoi} pèse ${poidsMo(fichier.size)} : la limite est de ${limiteMo(max)}. ${f.conseil}`;
  // Le contenu, pas l'extension : un exécutable renommé « cin.pdf » n'est pas
  // un PDF, et le serveur le refusera de toute façon — autant le dire tout de
  // suite, et le dire juste.
  const entete = new Uint8Array(await fichier.slice(0, 16).arrayBuffer());
  const reconnu = (f.formats as readonly string[]).some((k) => SIGNATURES[k](entete));
  if (!reconnu) return `${f.refus} Le fichier choisi n’en est pas un, quel que soit son nom.`;
  return null;
}
